/**
 * Vendor Dashboard - Main UI Component
 *
 * Displays list of vendors for the Vendor Certification Program:
 * - Vendor cards with certification status
 * - Add new vendor button
 * - Filter/search capabilities
 * - Quick actions (view, assess, archive)
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-528
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserDropdown } from '../../components/auth/UserDropdown';
import '../../styles/vendor.css';

// ============================================================================
// TYPES
// ============================================================================

type VendorStatus = 'active' | 'inactive' | 'archived';
type CertificationTier = 'platinum' | 'gold' | 'silver' | 'bronze' | null;

interface TierDistribution {
  platinum: number;
  gold: number;
  silver: number;
  bronze: number;
  none: number;
}

interface ExpiringCertification {
  vendor_id: string;
  vendor_name: string;
  product_name: string;
  tier: CertificationTier;
  expires_at: string;
  days_until_expiry: number;
}

interface ActivityEvent {
  event_id: string;
  action_type: string;
  entity_name: string;
  actor_name: string;
  created_at: string;
}

interface DashboardStats {
  total_vendors: number;
  certified_vendors: number;
  expiring_soon: number;
  pending_assessments: number;
  tier_distribution: TierDistribution;
}

interface Vendor {
  vendor_id: string;
  name: string;
  legal_entity?: string;
  headquarters_country?: string;
  headquarters_city?: string;
  website?: string;
  contact_name?: string;
  contact_email?: string;
  logo_url?: string;
  description?: string;
  status: VendorStatus;
  created_at: string;
  updated_at: string;
  // Computed from certifications
  certification_tier?: CertificationTier;
  certification_expires?: string;
  products_count?: number;
  assessments_count?: number;
}

interface VendorListResponse {
  vendors: Vendor[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

// ============================================================================
// TIER CONFIGURATION
// ============================================================================

const TIER_COLORS: Record<string, string> = {
  platinum: '#16a34a',
  gold: '#ca8a04',
  silver: '#6b7280',
  bronze: '#b45309',
  none: '#d1d5db',
};

const TIER_LABELS: Record<string, string> = {
  platinum: 'Platinum',
  gold: 'Gold',
  silver: 'Silver',
  bronze: 'Bronze',
  none: 'Not Certified',
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const CertificationBadge: React.FC<{ tier: CertificationTier; expires?: string }> = ({ tier, expires }) => {
  if (!tier) {
    return <span className="cert-badge cert-none">Not Certified</span>;
  }

  const isExpiringSoon = expires && new Date(expires) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  return (
    <span className={`cert-badge cert-${tier} ${isExpiringSoon ? 'expiring' : ''}`}>
      {tier.charAt(0).toUpperCase() + tier.slice(1)}
      {isExpiringSoon && <span className="expiring-indicator" title="Expires within 30 days">⚠</span>}
    </span>
  );
};

const VendorCard: React.FC<{
  vendor: Vendor;
  onView: (id: string) => void;
  onAssess: (id: string) => void;
  onArchive: (id: string) => void;
}> = ({ vendor, onView, onAssess, onArchive }) => {
  const location = [vendor.headquarters_city, vendor.headquarters_country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="vendor-card" onClick={() => onView(vendor.vendor_id)}>
      <div className="vendor-card-header">
        <div className="vendor-logo">
          {vendor.logo_url ? (
            <img src={vendor.logo_url} alt={vendor.name} />
          ) : (
            <span className="vendor-logo-placeholder">
              {vendor.name.substring(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        <div className="vendor-info">
          <h3 className="vendor-name">{vendor.name}</h3>
          {vendor.legal_entity && (
            <span className="vendor-legal">{vendor.legal_entity}</span>
          )}
        </div>
        <CertificationBadge
          tier={vendor.certification_tier || null}
          expires={vendor.certification_expires}
        />
      </div>

      <div className="vendor-card-body">
        {location && (
          <div className="vendor-location">
            <span className="icon">📍</span> {location}
          </div>
        )}
        {vendor.description && (
          <p className="vendor-description">{vendor.description}</p>
        )}
        <div className="vendor-stats">
          <span className="stat">
            <strong>{vendor.products_count || 0}</strong> Products
          </span>
          <span className="stat">
            <strong>{vendor.assessments_count || 0}</strong> Assessments
          </span>
        </div>
      </div>

      <div className="vendor-card-actions" onClick={(e) => e.stopPropagation()}>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => onView(vendor.vendor_id)}
        >
          View Details
        </button>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => onAssess(vendor.vendor_id)}
        >
          New Assessment
        </button>
        {vendor.status === 'active' && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => onArchive(vendor.vendor_id)}
            title="Archive vendor"
          >
            Archive
          </button>
        )}
      </div>
    </div>
  );
};

const EmptyState: React.FC<{ onAddVendor: () => void }> = ({ onAddVendor }) => (
  <div className="empty-state">
    <div className="empty-state-icon">🏢</div>
    <h2>No Vendors Yet</h2>
    <p>
      Start building your vendor certification program by adding your first vendor.
      You can then assess their data products for FAIR compliance and ML readiness.
    </p>
    <button className="btn btn-primary" onClick={onAddVendor}>
      Add Your First Vendor
    </button>
  </div>
);

// ============================================================================
// DONUT CHART COMPONENT (Phase 6.1)
// ============================================================================

const TierDonutChart: React.FC<{
  distribution: TierDistribution;
  onTierClick?: (tier: string) => void;
}> = ({ distribution, onTierClick }) => {
  const total = Object.values(distribution).reduce((sum, val) => sum + val, 0);
  if (total === 0) {
    return (
      <div className="donut-chart-empty">
        <p>No certification data</p>
      </div>
    );
  }

  const tiers = ['platinum', 'gold', 'silver', 'bronze', 'none'] as const;
  const size = 180;
  const strokeWidth = 30;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const centerX = size / 2;
  const centerY = size / 2;

  let currentOffset = 0;

  return (
    <div className="donut-chart-container">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {tiers.map((tier) => {
          const value = distribution[tier];
          if (value === 0) return null;

          const percentage = value / total;
          const dashLength = circumference * percentage;
          const offset = currentOffset;
          currentOffset += dashLength;

          return (
            <circle
              key={tier}
              cx={centerX}
              cy={centerY}
              r={radius}
              fill="none"
              stroke={TIER_COLORS[tier]}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dashLength} ${circumference - dashLength}`}
              strokeDashoffset={-offset}
              transform={`rotate(-90 ${centerX} ${centerY})`}
              style={{ cursor: onTierClick ? 'pointer' : 'default' }}
              onClick={() => onTierClick?.(tier)}
            />
          );
        })}
        {/* Center text */}
        <text
          x={centerX}
          y={centerY - 8}
          textAnchor="middle"
          fontSize="28"
          fontWeight="700"
          fill="#1f2937"
        >
          {total}
        </text>
        <text
          x={centerX}
          y={centerY + 14}
          textAnchor="middle"
          fontSize="12"
          fill="#6b7280"
        >
          Total
        </text>
      </svg>
      <div className="donut-legend">
        {tiers.map((tier) => (
          <div
            key={tier}
            className="legend-item"
            onClick={() => onTierClick?.(tier)}
            style={{ cursor: onTierClick ? 'pointer' : 'default' }}
          >
            <span
              className="legend-color"
              style={{ backgroundColor: TIER_COLORS[tier] }}
            />
            <span className="legend-label">{TIER_LABELS[tier]}</span>
            <span className="legend-value">{distribution[tier]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// UPCOMING RENEWALS PANEL (Phase 6.2)
// ============================================================================

const UpcomingRenewalsPanel: React.FC<{
  renewals: ExpiringCertification[];
  onRenewalClick?: (vendorId: string) => void;
}> = ({ renewals, onRenewalClick }) => {
  if (renewals.length === 0) {
    return (
      <div className="renewals-panel renewals-empty">
        <h3>Upcoming Renewals</h3>
        <p className="empty-message">No certifications expiring soon.</p>
      </div>
    );
  }

  const getUrgencyClass = (days: number): string => {
    if (days <= 0) return 'urgent-expired';
    if (days <= 30) return 'urgent-critical';
    if (days <= 60) return 'urgent-warning';
    return 'urgent-normal';
  };

  return (
    <div className="renewals-panel">
      <h3>Upcoming Renewals</h3>
      <div className="renewals-list">
        {renewals.slice(0, 5).map((renewal) => (
          <div
            key={`${renewal.vendor_id}-${renewal.product_name}`}
            className={`renewal-item ${getUrgencyClass(renewal.days_until_expiry)}`}
            onClick={() => onRenewalClick?.(renewal.vendor_id)}
          >
            <div className="renewal-info">
              <span className="renewal-vendor">{renewal.vendor_name}</span>
              <span className="renewal-product">{renewal.product_name}</span>
            </div>
            <div className="renewal-countdown">
              {renewal.days_until_expiry <= 0 ? (
                <span className="expired-badge">Expired</span>
              ) : (
                <>
                  <span className="days-value">{renewal.days_until_expiry}</span>
                  <span className="days-label">days</span>
                </>
              )}
            </div>
            <button className="btn btn-sm btn-secondary renewal-action">
              Renew
            </button>
          </div>
        ))}
      </div>
      {renewals.length > 5 && (
        <div className="renewals-more">
          <button className="btn btn-link">View All ({renewals.length})</button>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// RECENT ACTIVITY FEED (Phase 6.3)
// ============================================================================

const ACTION_ICONS: Record<string, string> = {
  assessment_created: '+',
  assessment_submitted: '→',
  assessment_approved: '✓',
  assessment_rejected: '✗',
  certification_issued: '★',
  certification_renewed: '↻',
  vendor_created: '+',
  default: '•',
};

const RecentActivityFeed: React.FC<{
  activities: ActivityEvent[];
  onActivityClick?: (eventId: string) => void;
}> = ({ activities, onActivityClick }) => {
  const formatRelativeTime = (dateStr: string): string => {
    const now = new Date();
    const then = new Date(dateStr);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (activities.length === 0) {
    return (
      <div className="activity-feed activity-empty">
        <h3>Recent Activity</h3>
        <p className="empty-message">No recent activity.</p>
      </div>
    );
  }

  return (
    <div className="activity-feed">
      <h3>Recent Activity</h3>
      <div className="activity-list">
        {activities.slice(0, 8).map((activity) => (
          <div
            key={activity.event_id}
            className="activity-item"
            onClick={() => onActivityClick?.(activity.event_id)}
          >
            <span className="activity-icon">
              {ACTION_ICONS[activity.action_type] || ACTION_ICONS.default}
            </span>
            <div className="activity-content">
              <span className="activity-action">
                {activity.action_type.replace(/_/g, ' ')}
              </span>
              <span className="activity-entity">{activity.entity_name}</span>
              <span className="activity-actor">by {activity.actor_name}</span>
            </div>
            <span className="activity-time">
              {formatRelativeTime(activity.created_at)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// PAGINATION COMPONENT (Phase 6.5)
// ============================================================================

const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (count: number) => void;
}> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        Showing {startItem}-{endItem} of {totalItems} vendors
      </div>
      <div className="pagination-controls">
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="items-per-page"
        >
          <option value={12}>12 per page</option>
          <option value={24}>24 per page</option>
          <option value={48}>48 per page</option>
        </select>
        <div className="page-buttons">
          <button
            className="btn btn-sm"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
          >
            Previous
          </button>
          {[...Array(Math.min(5, totalPages))].map((_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <button
                key={pageNum}
                className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : ''}`}
                onClick={() => onPageChange(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            className="btn btn-sm"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

const AddVendorModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (vendor: Partial<Vendor>) => Promise<void>;
}> = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    legal_entity: '',
    headquarters_country: '',
    headquarters_city: '',
    website: '',
    contact_name: '',
    contact_email: '',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Vendor name is required');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit(formData);
      setFormData({
        name: '',
        legal_entity: '',
        headquarters_country: '',
        headquarters_city: '',
        website: '',
        contact_name: '',
        contact_email: '',
        description: '',
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create vendor');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add New Vendor</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-message">{error}</div>}

            <div className="form-group">
              <label htmlFor="name">Vendor Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Acme Data Solutions"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="legal_entity">Legal Entity</label>
              <input
                id="legal_entity"
                type="text"
                value={formData.legal_entity}
                onChange={(e) => setFormData({ ...formData, legal_entity: e.target.value })}
                placeholder="e.g., Acme Inc."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="headquarters_city">City</label>
                <input
                  id="headquarters_city"
                  type="text"
                  value={formData.headquarters_city}
                  onChange={(e) => setFormData({ ...formData, headquarters_city: e.target.value })}
                  placeholder="e.g., Boston"
                />
              </div>
              <div className="form-group">
                <label htmlFor="headquarters_country">Country</label>
                <input
                  id="headquarters_country"
                  type="text"
                  value={formData.headquarters_country}
                  onChange={(e) => setFormData({ ...formData, headquarters_country: e.target.value })}
                  placeholder="e.g., USA"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://example.com"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contact_name">Contact Name</label>
                <input
                  id="contact_name"
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="e.g., John Smith"
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact_email">Contact Email</label>
                <input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the vendor and their data products..."
                rows={3}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Add Vendor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const VendorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<VendorStatus | 'all'>('active');
  const [tierFilter, setTierFilter] = useState<CertificationTier | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showAddModal, setShowAddModal] = useState(false);

  // Pagination state (Phase 6.5)
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Dashboard data (Phase 6.1-6.3)
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [upcomingRenewals, setUpcomingRenewals] = useState<ExpiringCertification[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityEvent[]>([]);

  // Open user guide in Typora/default markdown viewer
  const handleOpenGuide = async () => {
    try {
      await fetch('/open-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidePath: 'Vendor_Certification_Dashboard_Guide.md' })
      });
    } catch (err) {
      console.error('[VendorDashboard] Error opening guide:', err);
    }
  };

  // Fetch dashboard stats, renewals, and activity (Phase 6.1-6.3)
  const fetchDashboardData = useCallback(async () => {
    try {
      // Fetch stats
      const statsRes = await fetch('/api/vendors/stats');
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setDashboardStats(stats);
      }

      // Fetch upcoming renewals
      const renewalsRes = await fetch('/api/vendors/renewals?days=90');
      if (renewalsRes.ok) {
        const renewals = await renewalsRes.json();
        setUpcomingRenewals(renewals.renewals || []);
      }

      // Fetch recent activity
      const activityRes = await fetch('/api/vendors/activity?limit=10');
      if (activityRes.ok) {
        const activity = await activityRes.json();
        setRecentActivity(activity.events || []);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (tierFilter !== 'all') {
        params.append('tier', tierFilter || 'none');
      }
      if (categoryFilter !== 'all') {
        params.append('category', categoryFilter);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      params.append('page', String(currentPage));
      params.append('limit', String(itemsPerPage));
      params.append('sort', sortBy);
      params.append('order', sortOrder);

      const response = await fetch(`/api/vendors?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch vendors');
      }

      const data: VendorListResponse = await response.json();
      setVendors(data.vendors);
      setTotalItems(data.total);
      setTotalPages(data.total_pages || Math.ceil(data.total / itemsPerPage));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, tierFilter, categoryFilter, searchQuery, currentPage, itemsPerPage, sortBy, sortOrder]);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, tierFilter, categoryFilter, searchQuery]);

  // Handle tier filter from donut chart click
  const handleTierFilterFromChart = (tier: string) => {
    setTierFilter(tier === 'none' ? null : tier as CertificationTier);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (count: number) => {
    setItemsPerPage(count);
    setCurrentPage(1);
  };

  const handleAddVendor = async (vendorData: Partial<Vendor>) => {
    const response = await fetch('/api/vendors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(vendorData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create vendor');
    }

    await fetchVendors();
  };

  const handleViewVendor = (vendorId: string) => {
    navigate(`/vendors/${vendorId}`);
  };

  const handleAssessVendor = (vendorId: string) => {
    navigate(`/vendors/${vendorId}/assess`);
  };

  const handleArchiveVendor = async (vendorId: string) => {
    if (!confirm('Are you sure you want to archive this vendor?')) {
      return;
    }

    try {
      const response = await fetch(`/api/vendors/${vendorId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'archived' }),
      });

      if (!response.ok) {
        throw new Error('Failed to archive vendor');
      }

      await fetchVendors();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive vendor');
    }
  };

  // Summary stats (use dashboard stats if available, fallback to client-side calculation)
  const totalVendors = dashboardStats?.total_vendors ?? totalItems;
  const certifiedVendors = dashboardStats?.certified_vendors ?? vendors.filter((v) => v.certification_tier).length;
  const expiringCerts = dashboardStats?.expiring_soon ?? vendors.filter((v) => {
    if (!v.certification_expires) return false;
    const expiresDate = new Date(v.certification_expires);
    const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return expiresDate < thirtyDaysFromNow;
  }).length;
  const _pendingAssessments = dashboardStats?.pending_assessments ?? 0;

  // Default tier distribution if not available from API
  const tierDistribution: TierDistribution = dashboardStats?.tier_distribution ?? {
    platinum: vendors.filter(v => v.certification_tier === 'platinum').length,
    gold: vendors.filter(v => v.certification_tier === 'gold').length,
    silver: vendors.filter(v => v.certification_tier === 'silver').length,
    bronze: vendors.filter(v => v.certification_tier === 'bronze').length,
    none: vendors.filter(v => !v.certification_tier).length,
  };

  return (
    <div className="vendor-dashboard">
      {/* Breadcrumb Navigation */}
      <nav className="vendor-breadcrumb">
        <button className="breadcrumb-link" onClick={() => navigate('/home')}>
          Home
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Vendor Certification</span>
      </nav>

      <div className="dashboard-header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h1>Vendor Certification Program</h1>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleOpenGuide();
              }}
              className="help-link"
              title="Open Vendor Certification Dashboard User Guide"
              style={{
                fontSize: '1.25rem',
                textDecoration: 'none',
                color: '#3b82f6',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dbeafe';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#eff6ff';
                e.currentTarget.style.borderColor = '#bfdbfe';
              }}
            >
              ?
            </a>
          </div>
          <p className="subtitle">
            Manage vendors and assess their data products for FAIR compliance
          </p>
        </div>
        <div className="header-actions" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            + Add Vendor
          </button>
          <UserDropdown />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-value">{totalVendors}</div>
          <div className="summary-label">Total Vendors</div>
        </div>
        <div className="summary-card">
          <div className="summary-value">{certifiedVendors}</div>
          <div className="summary-label">Certified</div>
        </div>
        <div className="summary-card warning">
          <div className="summary-value">{expiringCerts}</div>
          <div className="summary-label">Expiring Soon</div>
        </div>
      </div>

      {/* Dashboard Panels - Donut Chart, Renewals, Activity (Phase 6.1-6.3) */}
      <div className="dashboard-panels">
        <div className="dashboard-panel-left">
          <div className="panel-card">
            <h3>Certification Distribution</h3>
            <TierDonutChart
              distribution={tierDistribution}
              onTierClick={handleTierFilterFromChart}
            />
          </div>
        </div>
        <div className="dashboard-panel-center">
          <UpcomingRenewalsPanel
            renewals={upcomingRenewals}
            onRenewalClick={(vendorId) => navigate(`/vendors/${vendorId}`)}
          />
        </div>
        <div className="dashboard-panel-right">
          <RecentActivityFeed activities={recentActivity} />
        </div>
      </div>

      {/* Filters (Phase 6.4) */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search vendors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as VendorStatus | 'all')}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="filter-group">
          <select
            value={tierFilter ?? 'all'}
            onChange={(e) => {
              const val = e.target.value;
              setTierFilter(val === 'all' ? 'all' : val === 'none' ? null : val as CertificationTier);
            }}
          >
            <option value="all">All Tiers</option>
            <option value="platinum">Platinum</option>
            <option value="gold">Gold</option>
            <option value="silver">Silver</option>
            <option value="bronze">Bronze</option>
            <option value="none">Not Certified</option>
          </select>
        </div>
        <div className="filter-group">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All Categories</option>
            <option value="Drug Discovery">Drug Discovery</option>
            <option value="Clinical Trial">Clinical Trial</option>
            <option value="Real World Evidence">Real World Evidence</option>
            <option value="Pharmacovigilance">Pharmacovigilance</option>
            <option value="Manufacturing">Manufacturing</option>
            <option value="Regulatory">Regulatory</option>
            <option value="Genomics">Genomics</option>
            <option value="Biomarker">Biomarker</option>
            <option value="Medical Imaging">Medical Imaging</option>
            <option value="NLP/Text Mining">NLP/Text Mining</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="filter-group sort-group">
          <label className="filter-label">Sort:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name">Name</option>
            <option value="created_at">Date Added</option>
            <option value="updated_at">Last Updated</option>
            <option value="certification_tier">Tier</option>
          </select>
          <button
            className="sort-order-btn"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
        {(statusFilter !== 'active' || tierFilter !== 'all' || categoryFilter !== 'all' || searchQuery) && (
          <button
            className="btn btn-text"
            onClick={() => {
              setStatusFilter('active');
              setTierFilter('all');
              setCategoryFilter('all');
              setSearchQuery('');
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading vendors...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={fetchVendors}>
            Try Again
          </button>
        </div>
      ) : vendors.length === 0 ? (
        <EmptyState onAddVendor={() => setShowAddModal(true)} />
      ) : (
        <>
          <div className="vendor-grid">
            {vendors.map((vendor) => (
              <VendorCard
                key={vendor.vendor_id}
                vendor={vendor}
                onView={handleViewVendor}
                onAssess={handleAssessVendor}
                onArchive={handleArchiveVendor}
              />
            ))}
          </div>
          {/* Pagination (Phase 6.5) */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </>
      )}

      {/* Add Vendor Modal */}
      <AddVendorModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddVendor}
      />
    </div>
  );
};

export default VendorDashboard;
